import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import parse, { attributesToProps } from 'html-react-parser'

import { CONTENT_TYPES } from 'Common/constants/contentTypes'
import { MoveItemModal } from 'Packages/folders'
import LightboxImage from 'Common/components/LightboxImage'
import Breadcrumbs from 'Common/components/Breadcrumbs'
import {
  Box,
  Col,
  Columns,
  Container,
  Icon,
  Heading,
  MonoButton,
  useToast,
} from '@redriverpress/system'
import DOMPurify from 'dompurify'

import getLevelsByType from 'Common/utils/getLevelsByType'
import useUserAccess from 'Common/hooks/useUserAccess'

import VideoAccessDialog from '../common/components/VideoAccessDialog'
import getVideo from './services/getVideo'

import VideoPlayerSkeleton from './components/skeletons/VideoPlayerSkeleton'
import TeacherDiscretion from './components/TeacherDiscretion'
import LessonCard from './components/LessonCard'
import VideoInfo from './components/VideoInfo'
import WatchNextSection from './components/WatchNextSection'

// Allow iframes and the frameborder attribute;
// we need these for embedding videos
// See DOMPurify docs: https://github.com/cure53/DOMPurify#can-i-configure-dompurify
const sanitizeOptions = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['frameborder', 'allowfullscreen', 'allow'],
}

// Replace img tags with the LightboxImage component
const parseOptions = {
  replace: domNode => {
    if (domNode.name === 'img') {
      const { src, alt } = attributesToProps(domNode.attribs)
      return <LightboxImage src={src} alt={alt} />
    }
  },
}

export default function VideoPage() {
  const {
    userIsLoading,
    userHasAccess,
    userLoggedIn,
    userData,
  } = useUserAccess()
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const { videoParam } = useParams()
  const [videoData, setVideoData] = useState(null)
  const [moveItemModalOpen, setMoveItemModalOpen] = useState(false)

  const { addToast } = useToast()

  const goBackToGallery = (isOpen = false) => {
    if (!isOpen) window.location.href = '/media/videos'
  }

  const content = `<div class='videoWrapper'><iframe title='video-iframe' allow='autoplay;fullscreen' allowfullscreen='' frameborder='0' height='360' src='https://player.vimeo.com/video/${videoData?.vimeoVideoId}' width='640'></iframe></div>`

  const sanitizedContent = DOMPurify.sanitize(content, sanitizeOptions)

  const levels = getLevelsByType(userData?.levelPreference, videoData)
  useEffect(() => {
    if (!userIsLoading) {
      if (userLoggedIn && userHasAccess) {
        getVideo(videoParam).then(
          res => {
            setVideoData(res.data)
          },
          errors => {
            addToast({
              id: errors,
              content: 'Something went wrong',
              type: 'warning',
              action: (
                <MonoButton
                  small
                  mr={-2}
                  onClick={() => window.location.reload()}
                >
                  <Icon icon='redo' pr={2} />
                  Retry
                </MonoButton>
              ),
            })
          }
        )
      } else {
        setIsAccessDialogOpen(true)
      }
    }
  }, [userIsLoading])

  return (
    <>
      <Box py={[3, null, null, 4]} pb={[4, null, null, 5]} bg='gray100'>
        <Container>
          <Columns>
            {/* Video Column */}
            <Col width={[1, null, null, 8 / 12]}>
              {videoData ? (
                <>
                  <Breadcrumbs
                    routes={[
                      {
                        label: 'Videos',
                        to: '/media/videos',
                        external: true,
                      },
                      {
                        label: videoData.genre.title,
                        to: `/media/videos/${videoData.genre.slug}`,
                        external: true,
                      },
                      {
                        label: videoData.title,
                        to: '',
                        external: true,
                      },
                    ]}
                  />

                  {/* Video Player */}
                  <Box mb={4}>{parse(sanitizedContent, parseOptions)}</Box>

                  <VideoInfo
                    id={videoData.id}
                    title={videoData.title}
                    subtitle={videoData.subtitle}
                    date={videoData.date}
                    levels={levels}
                    description={videoData.description}
                    categoryTitle={videoData.category.title}
                    onSaveToFolder={() => setMoveItemModalOpen(true)}
                  />
                </>
              ) : (
                <VideoPlayerSkeleton />
              )}

              {/* Dicretion box */}
              {videoData?.discretion && (
                <TeacherDiscretion
                  mt={3}
                  p={3}
                  discretionText={videoData.discretionText}
                />
              )}

              {/* Found In section */}
              {videoData?.lessons.length > 0 && (
                <Box mt={3}>
                  <Heading
                    as='h1'
                    fontSize={[2, null, 4]}
                    lineHeight='heading'
                    fontWeight='600'
                    mb={3}
                  >
                    Found in
                  </Heading>

                  <Box
                    display='grid'
                    gridTemplateColumns={['1fr', null, 'repeat(2, 1fr)']}
                    gridColumnGap={3}
                    gridRowGap={3}
                    mb={4}
                  >
                    {videoData.lessons.map(lesson => (
                      <LessonCard key={lesson.id} lesson={lesson} />
                    ))}
                  </Box>
                </Box>
              )}
            </Col>

            {/* Watch Next Column */}
            <Col width={[1, null, null, 4 / 12]}>
              <WatchNextSection
                ml={[0, null, null, 3]}
                mt={[4, null, null, 0]}
                playlist={videoData?.playlist}
                showSkeleton={!videoData}
              />
            </Col>
          </Columns>
        </Container>
      </Box>

      {/* Get Access Modal */}
      <VideoAccessDialog
        open={isAccessDialogOpen}
        onOpenChange={isOpen => goBackToGallery(isOpen)}
        userLoggedIn={userLoggedIn}
      />

      {/* Save to folder modal */}

      {!!videoData && (
        <MoveItemModal
          isOpen={moveItemModalOpen}
          item={{
            fileable: {
              id: videoData?.id,
              type: CONTENT_TYPES.video,
            },
          }}
          onClose={reload => {
            setMoveItemModalOpen(false)
            reload && window.location.reload()
          }}
        />
      )}
    </>
  )
}
